import { FC, useEffect, useState, useRef } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import { useMediaQuery } from '@mui/material';

import { useRecoilValue } from 'recoil';
import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
// Import Swiper styles
import 'swiper/css';

import { useDirectChatPath } from '@hooks_iqfu/public/useDirectChatPath';
import { useInjectLeasebackCaution } from '@hooks_iqfu/public/useInjectLeasebackCaution';
import { useLikedCompanies } from '@hooks_iqfu/public/useLikedCompanies';

import RankIconLeft from '/public/images/iqfu/public/rank_icon_left.svg';
import RankIconRight from '/public/images/iqfu/public/rank_icon_right.svg';

import { formatCompanyCardText } from '@utils_iqfu/formatCompanyCardText';

import DisplayThumbImage from '@components_iqfu/public/display_thumb_image/DisplayThumbImage';

import { sellerIsLoggedIn } from '@recoil_iqfu/atoms/public/login_state';
import userUuidValue from '@recoil_iqfu/atoms/public/user_uuid';

import { CompanyListItem } from '@type-def_iqfu/public/CompanyListItem';
import { likedCompanyData } from '@type-def_iqfu/public/likedCompaniesData';

import { MediaQuery } from '@constants_common';
import { saleCaseTypeText } from '@constants_iqfu';

import styles from './CompanyCardsCarousel.module.scss';

type Props = {
  data?: CompanyListItem[];
  hasRank?: boolean;
  pageLabel: string;
};

const CompanyCardsCarousel: FC<Props> = ({ data, hasRank = true, pageLabel }) => {
  const [prevEl, setPrevEl] = useState<HTMLElement | null>(null);
  const [nextEl, setNextEl] = useState<HTMLElement | null>(null);
  const isMobile = useMediaQuery(MediaQuery.lg);

  const [swiper, setSwiper] = useState<any>(null);

  //ページ遷移時にスライドをリセット
  useEffect(() => {
    if (swiper && data && data.length > 0) {
      swiper.slideTo(0, 0);
    }
  }, [data, swiper]);

  return (
    <div className={styles.container}>
      <Swiper
        modules={[Navigation]}
        navigation={{
          prevEl,
          nextEl,
        }}
        threshold={10}
        spaceBetween={0}
        slidesPerView={1}
        onUpdate={(swiper) => {
          setSwiper(swiper);
        }}
        breakpoints={{
          992: {
            slidesPerView: 3,
            spaceBetween: 0,
          },
        }}
      >
        {data &&
          data.map((company, i) => (
            <SwiperSlide key={company.id}>
              {hasRank ? (
                <CompanyCard data={company} rank={i + 1} pageLabel={pageLabel} />
              ) : (
                <CompanyCard data={company} pageLabel={pageLabel} />
              )}
            </SwiperSlide>
          ))}
      </Swiper>

      {((data && data.length > 1 && isMobile) || (data && data.length > 3 && !isMobile)) && (
        <div className={styles.sliderNavigation}>
          <div ref={(node) => setPrevEl(node)} className={styles.sliderPrev}></div>
          <div ref={(node) => setNextEl(node)} className={styles.sliderNext}></div>
        </div>
      )}

      {/* end of slider */}
    </div>
  );
};

const getOtherData = (data: CompanyListItem) => {
  const access = data.overview_items.filter((item) => item.name === 'アクセス')[0]?.content;
  const holiday = data.overview_items.filter((item) => item.name === '定休日')[0]?.content;
  const businessHours = data.overview_items.filter((item) => item.name === '営業時間')[0]?.content;
  const parking = data.overview_items.filter((item) => item.name === '駐車場')[0]?.content;
  return {
    access,
    holiday,
    businessHours,
    parking,
  };
};

type CompanyCardProps = {
  data: CompanyListItem;
  rank?: number;
  pageLabel: string;
};

const CompanyCard: FC<CompanyCardProps> = ({ data, rank, pageLabel }) => {
  const router = useRouter();
  const userUuid = useRecoilValue(userUuidValue);
  const [isLiked, setIsLiked] = useState(false);
  const [otherData, setOtherData] = useState(getOtherData(data));
  const { dispatchAddLikedCompaniesAction, useGetLikedCompanies } = useLikedCompanies();
  const chatButtonRef = useRef<HTMLButtonElement>(null);

  const injectLeasebackCautions = useInjectLeasebackCaution();
  const getDirectChatPath = useDirectChatPath();

  const isLoggedIn = useRecoilValue(sellerIsLoggedIn);

  const { data: fetchedLikedCompaniesData } = useGetLikedCompanies({
    isLoggedIn,
    userUuid,
  });

  const likedCompanies: likedCompanyData[] = fetchedLikedCompaniesData?.favorite_companies;

  const checkIsLiked = () => {
    return likedCompanies?.filter((company) => company.company_id === data.id).length > 0;
  };

  useEffect(() => {
    if (setIsLiked) {
      setIsLiked(checkIsLiked());
    }
  }, [likedCompanies]);

  let companyHasRankClass = '';
  let companyRankClass = '';
  if (rank) {
    companyHasRankClass = styles.hasRank;
    if (rank < 4) {
      companyRankClass = styles[`rank_${rank}`];
    } else {
      companyRankClass = styles.rank_others;
    }
  }

  const directChatButtonClickHandler = (companyId: number) => {
    const stopProcessing = injectLeasebackCautions();
    if (stopProcessing) return;

    if (!isLiked) {
      dispatchAddLikedCompaniesAction({
        isLoggedIn: isLoggedIn,
        userUuid: userUuid,
        companyId: companyId,
        component: 'CompanyCardsCarousel',
        pageLabel: pageLabel,
      });
    }
    router.push(getDirectChatPath());
  };

  const isMobile = useMediaQuery(MediaQuery.lg);

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.closest('button') === chatButtonRef.current) {
      e.preventDefault();
      e.stopPropagation();
      directChatButtonClickHandler(data.id);
    }
  };

  return (
    <Link
      className={styles.sliderItemWrapper}
      href={`/companies/${data.id}`}
      target={isMobile ? '_self' : '_blank'}
      onClick={handleCardClick}
    >
      <div className={styles.sliderItem}>
        <h4 className={`${styles.company_name} ${companyHasRankClass} ${companyRankClass}`}>
          {rank && (
            <span className={styles.rankMark}>
              <span className={styles.rankIcon_left}>
                <RankIconLeft />
              </span>
              <span className={styles.rankMark_num}>{rank}</span>位
              <span className={styles.rankIcon_right}>
                <RankIconRight />
              </span>
            </span>
          )}
          {data.display_name}
        </h4>
        <div className={styles.company_image}>
          {data.main_image ? (
            <DisplayThumbImage originalImagePath={data.main_image} thumbSize="medium" lazyLoading={true} />
          ) : (
            <div className={styles.no_image}>
              <img
                className={styles.company_img}
                src={'/images/iqfu/public/no_company_image.png'}
                alt="準備中"
                loading="lazy"
              />
            </div>
          )}
        </div>

        <div className={styles.info}>
          <h5 className={styles.card_lead}>{data.cassette_title}</h5>

          <>
            {data.tags && data.tags.length > 0 ? (
              <ul className={styles.tag_container}>
                {data.tags.map((tag, i) => {
                  return (
                    <li className={styles.tag_item} key={i}>
                      {tag.name}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <></>
            )}
            {data.within_36_months_sale_case_counts && data.within_36_months_sale_case_counts.count > 0 ? (
              <div>
                <p className={styles.card_sale_case_count}>
                  <span className={styles.bold}>
                    {saleCaseTypeText[data.within_36_months_sale_case_counts.sort_type] ?? '売却実績数'}
                  </span>
                  <span className={`${styles.orange} ${styles.bold}`}>
                    <span className={styles.number}>{data.within_36_months_sale_case_counts.count}</span>件{' '}
                  </span>
                  <span className={styles.annotate}>※3年以内</span>
                </p>
              </div>
            ) : (
              <></>
            )}

            <table className={styles.other_data_container}>
              <tbody>
                <tr>
                  <th className={styles.other_data_title}>所在地</th>
                  <td className={styles.other_data_content}>{formatCompanyCardText(data.full_address, 36) || '-'}</td>
                </tr>

                <tr>
                  <th className={styles.other_data_title}>アクセス</th>
                  <td className={styles.other_data_content}>{formatCompanyCardText(otherData.access, 36) || '-'}</td>
                </tr>
              </tbody>
            </table>

            <div className={styles.buttons_container}>
              <div className={styles.detail_button}>詳しくみる</div>
              <button ref={chatButtonRef} className={styles.chat_button}>
                <span className={styles.chat_button_small}>無料</span>
                <span>売却相談する</span>
              </button>
            </div>
          </>
        </div>
      </div>
    </Link>
  );
};

export default CompanyCardsCarousel;
